import {
  CreateQuoteDocument,
  ImportDocumentRequest,
  QuoteDocumentConfig,
  QuoteDocumentConfigPositionEnum,
  UpdateQuoteDocument,
} from '@monetize/types/app';
import {
  ChangeEvent,
  DragEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { handleApiErrorToast } from '../../../../../api/axios';
import { MBox, MFlex, MText } from '../../../../../components/Monetize';
import { ACCEPT_FILES } from '../../../../../constants/misc';
import {
  NewQuoteDocumentUpload,
  useQuoteDocumentUploads,
} from '../../../../../hooks/useQuoteDocumentUploads';
import { useFlags } from '../../../../../services/launchDarkly';
import { QuoteDocumentUpload } from '../../../../../store/quoteDocumentUpload.store';
import { pluralize } from '../../../../../utils';
import {
  QUOTE_DIVIDER,
  QuoteDocumentListItem,
  SortPositionReserver,
  buildReorderPayload,
  createSortPositionReserver,
  haveSameOrder,
  insertBySortOrder,
  isQuoteDivider,
} from '../quoteDocumentsListUtils';
import {
  MAX_FILE_SIZE_BYTES,
  QuoteDocumentsMode,
  ROW_GRID_MIN_WIDTH,
  SETTINGS_ROW_GRID_MIN_WIDTH,
} from './constants';
import { DropZoneOverlay, EmptyRegionPlaceholder } from './DropZoneIndicators';
import { PendingDocumentRow } from './PendingDocumentRow';
import { QuoteDocumentListHeader } from './QuoteDocumentListHeader';
import { QuoteDocumentRow } from './QuoteDocumentRow';
import { QuoteDocumentsToolbar } from './QuoteDocumentsToolbar';

// Keyed by the original doc's id — the replacement create produces a new
// doc with a different id, so this entry naturally stops matching anything
// once the server reflects the swap and gets cleared once onReplaceDocument
// settles.
interface ActiveReplacement {
  doc: QuoteDocumentConfig;
  file: File;
}

type DragZone = 'before' | 'after';

interface QuoteDocumentsListSharedProps {
  /** Id of the quote or legal entity this document list belongs to — scopes pending-upload state. */
  ownerId: string;
  isReadOnly: boolean;
  beforeDocuments: QuoteDocumentConfig[];
  afterDocuments: QuoteDocumentConfig[];
  quoteDocumentTypes: Array<{ id: string; label: string; value: string }>;
  onOpenDocument: (
    documentConfigId: string,
    filename?: string,
  ) => Promise<void>;
  onDeleteDocument: (documentConfigId: string) => void;
  onDownloadDocument: (doc: QuoteDocumentConfig) => Promise<void>;
  onChangeDocumentType: (
    doc: QuoteDocumentConfig,
    documentTypeId: string,
  ) => Promise<void>;
  onReplaceDocument: (doc: QuoteDocumentConfig, file: File) => Promise<void>;
  onCreateDocument: (data: CreateQuoteDocument, file: File) => Promise<void>;
  onUpdateDocumentConfigurations: (docs: UpdateQuoteDocument[]) => void;
}

interface QuoteModeProps extends QuoteDocumentsListSharedProps {
  mode: QuoteDocumentsMode.QUOTE;
  quoteId: string;
  availableDocuments: QuoteDocumentConfig[];
  isAvailableDocumentsLoading: boolean;
  isImportingDocuments: boolean;
  onOpenQuote: () => Promise<void>;
  onDownloadQuote: () => Promise<void>;
  onImportDocuments: (items: ImportDocumentRequest[]) => Promise<void>;
}

interface SettingsModeProps extends QuoteDocumentsListSharedProps {
  mode: QuoteDocumentsMode.SETTINGS;
  onToggleAutoAdd: (
    doc: QuoteDocumentConfig,
    checked: boolean,
  ) => Promise<void>;
}

export type QuoteDocumentsListProps = QuoteModeProps | SettingsModeProps;

export const QuoteDocumentsList: FC<QuoteDocumentsListProps> = (props) => {
  const {
    ownerId,
    isReadOnly,
    beforeDocuments,
    afterDocuments,
    quoteDocumentTypes,
    onOpenDocument,
    onDeleteDocument,
    onDownloadDocument,
    onChangeDocumentType,
    onReplaceDocument,
    onCreateDocument,
    onUpdateDocumentConfigurations,
    mode,
  } = props;

  const { enableDocxSupport } = useFlags();

  const {
    uploads: pendingUploads,
    addFiles: addPendingUploads,
    selectType: selectPendingUploadType,
    retry: retryPendingUpload,
    remove: removePendingUpload,
  } = useQuoteDocumentUploads(ownerId, onCreateDocument);

  const [orderedItems, setOrderedItems] = useState<QuoteDocumentListItem[]>([]);
  const orderedItemsRef = useRef<QuoteDocumentListItem[]>([]);
  const lastPersistedOrderRef = useRef<QuoteDocumentListItem[]>([]);
  const [replacingDoc, setReplacingDoc] = useState<QuoteDocumentConfig | null>(
    null,
  );
  const [activeReplacements, setActiveReplacements] = useState<
    Map<string, ActiveReplacement>
  >(new Map());
  const [changingTypeDocId, setChangingTypeDocId] = useState<string | null>(
    null,
  );
  const [togglingAutoAddDocId, setTogglingAutoAddDocId] = useState<
    string | null
  >(null);
  const [openingDocId, setOpeningDocId] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [isOpeningQuote, setIsOpeningQuote] = useState(false);
  const [isDownloadingQuote, setIsDownloadingQuote] = useState(false);
  const [dragZone, setDragZone] = useState<DragZone | null>(null);
  const dragCounterBeforeRef = useRef(0);
  const dragCounterAfterRef = useRef(0);
  const [sortPositionReserver] = useState<SortPositionReserver>(() =>
    createSortPositionReserver(),
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const uploadPositionRef = useRef<QuoteDocumentConfigPositionEnum>(
    QuoteDocumentConfigPositionEnum.AFTER,
  );

  const acceptedFileKeys = useMemo(
    () => (enableDocxSupport ? ['pdf', 'docx'] : ['pdf']),
    [enableDocxSupport],
  );
  const acceptedMimeTypes = useMemo(
    () => acceptedFileKeys.flatMap((key) => ACCEPT_FILES[key].accepted),
    [acceptedFileKeys],
  );
  const acceptAttr = useMemo(
    () => acceptedMimeTypes.join(','),
    [acceptedMimeTypes],
  );

  useEffect(() => {
    sortPositionReserver.sync(beforeDocuments, afterDocuments);

    // Replace's delete step removes the original doc from these props before
    // the create step's replacement lands — reinsert it so the row stays put
    // instead of disappearing and reappearing once the swap completes.
    let nextBefore = beforeDocuments;
    let nextAfter = afterDocuments;
    activeReplacements.forEach(({ doc }) => {
      const stillPresent =
        nextBefore.some((existing) => existing.id === doc.id) ||
        nextAfter.some((existing) => existing.id === doc.id);
      if (stillPresent) {
        return;
      }
      if (doc.position === QuoteDocumentConfigPositionEnum.BEFORE) {
        nextBefore = insertBySortOrder(nextBefore, doc);
      } else {
        nextAfter = insertBySortOrder(nextAfter, doc);
      }
    });

    const next: QuoteDocumentListItem[] = [
      ...nextBefore,
      QUOTE_DIVIDER,
      ...nextAfter,
    ];
    setOrderedItems(next);
    orderedItemsRef.current = next;
    lastPersistedOrderRef.current = next;
  }, [
    beforeDocuments,
    afterDocuments,
    activeReplacements,
    sortPositionReserver,
  ]);

  const handleMove = useCallback((dragIndex: number, hoverIndex: number) => {
    setOrderedItems((prev) => {
      const updated = [...prev];
      const [removed] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, removed);
      orderedItemsRef.current = updated;
      return updated;
    });
  }, []);

  const handleDropEnd = useCallback(() => {
    if (haveSameOrder(orderedItemsRef.current, lastPersistedOrderRef.current)) {
      return;
    }
    onUpdateDocumentConfigurations(
      buildReorderPayload(orderedItemsRef.current),
    );
  }, [onUpdateDocumentConfigurations]);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!acceptedMimeTypes.includes(file.type)) {
        return `${file.name} is not a supported file type.`;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return `${file.name} exceeds the 10MB size limit.`;
      }
      return null;
    },
    [acceptedMimeTypes],
  );

  const handleFilesSelected = useCallback(
    (files: FileList | File[], position: QuoteDocumentConfigPositionEnum) => {
      const newFiles: NewQuoteDocumentUpload[] = [];
      Array.from(files).forEach((file) => {
        const error = validateFile(file);
        if (error) {
          handleApiErrorToast(new Error(error));
          return;
        }
        newFiles.push({ file, position });
      });
      if (newFiles.length > 0) {
        addPendingUploads(newFiles);
      }
    },
    [validateFile, addPendingUploads],
  );

  const handleOpenFilePicker = (position: QuoteDocumentConfigPositionEnum) => {
    uploadPositionRef.current = position;
    fileInputRef.current?.click();
  };

  // Distinguishes an OS file drag from react-dnd's internal row-reorder drag
  // (both fire through the same native DragEvent stream) — only file drags
  // carry a "Files" data type.
  const isFileDrag = (event: DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer.types).includes('Files');

  const zoneCounterRef = (zone: DragZone) =>
    zone === 'before' ? dragCounterBeforeRef : dragCounterAfterRef;

  const handleZoneDragEnter =
    (zone: DragZone) => (event: DragEvent<HTMLDivElement>) => {
      if (isReadOnly || !isFileDrag(event)) {
        return;
      }
      event.preventDefault();
      zoneCounterRef(zone).current += 1;
      setDragZone(zone);
    };

  const handleZoneDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!isReadOnly && isFileDrag(event)) {
      event.preventDefault();
    }
  };

  const handleZoneDragLeave =
    (zone: DragZone) => (event: DragEvent<HTMLDivElement>) => {
      if (isReadOnly || !isFileDrag(event)) {
        return;
      }
      const ref = zoneCounterRef(zone);
      ref.current = Math.max(0, ref.current - 1);
      if (ref.current === 0) {
        setDragZone((prev) => (prev === zone ? null : prev));
      }
    };

  const handleZoneDrop =
    (zone: DragZone) => (event: DragEvent<HTMLDivElement>) => {
      if (!isFileDrag(event)) {
        return;
      }
      event.preventDefault();
      dragCounterBeforeRef.current = 0;
      dragCounterAfterRef.current = 0;
      setDragZone(null);
      if (isReadOnly) {
        return;
      }
      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        handleFilesSelected(
          event.dataTransfer.files,
          zone === 'before'
            ? QuoteDocumentConfigPositionEnum.BEFORE
            : QuoteDocumentConfigPositionEnum.AFTER,
        );
      }
    };

  const handlePendingTypeSelected = useCallback(
    (pending: QuoteDocumentUpload, documentTypeId: string) =>
      selectPendingUploadType(
        pending,
        documentTypeId,
        sortPositionReserver.reserveFor(pending.localId, pending.position),
      ),
    [selectPendingUploadType, sortPositionReserver],
  );

  const handleRetryPendingUpload = useCallback(
    (pending: QuoteDocumentUpload) =>
      retryPendingUpload(
        pending,
        sortPositionReserver.reserveFor(pending.localId, pending.position),
      ),
    [retryPendingUpload, sortPositionReserver],
  );

  const handleRequestReplace = (doc: QuoteDocumentConfig) => {
    setReplacingDoc(doc);
    replaceInputRef.current?.click();
  };

  const handleReplaceFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    const doc = replacingDoc;
    setReplacingDoc(null);
    event.target.value = '';
    if (!file || !doc) {
      return;
    }
    setActiveReplacements((prev) => new Map(prev).set(doc.id, { doc, file }));
    try {
      await onReplaceDocument(doc, file);
    } finally {
      setActiveReplacements((prev) => {
        const next = new Map(prev);
        next.delete(doc.id);
        return next;
      });
    }
  };

  const handleChangeType = async (
    doc: QuoteDocumentConfig,
    documentTypeId: string,
  ) => {
    setChangingTypeDocId(doc.id);
    try {
      await onChangeDocumentType(doc, documentTypeId);
    } finally {
      setChangingTypeDocId(null);
    }
  };

  const handleToggleAutoAdd = async (
    doc: QuoteDocumentConfig,
    checked: boolean,
  ) => {
    if (mode !== QuoteDocumentsMode.SETTINGS) {
      return;
    }
    const { onToggleAutoAdd } = props;
    setTogglingAutoAddDocId(doc.id);
    try {
      await onToggleAutoAdd(doc, checked);
    } finally {
      setTogglingAutoAddDocId(null);
    }
  };

  const handleOpenDocument = async (
    documentConfigId: string,
    filename?: string,
  ) => {
    setOpeningDocId(documentConfigId);
    try {
      await onOpenDocument(documentConfigId, filename);
    } finally {
      setOpeningDocId(null);
    }
  };

  const handleDownloadDocument = async (doc: QuoteDocumentConfig) => {
    setDownloadingDocId(doc.id);
    try {
      await onDownloadDocument(doc);
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleOpenQuote = async () => {
    if (mode !== QuoteDocumentsMode.QUOTE) {
      return;
    }
    const { onOpenQuote } = props;
    setIsOpeningQuote(true);
    try {
      await onOpenQuote();
    } finally {
      setIsOpeningQuote(false);
    }
  };

  const handleDownloadQuote = async () => {
    if (mode !== QuoteDocumentsMode.QUOTE) {
      return;
    }
    const { onDownloadQuote } = props;
    setIsDownloadingQuote(true);
    try {
      await onDownloadQuote();
    } finally {
      setIsDownloadingQuote(false);
    }
  };

  const dividerIndex = orderedItems.findIndex(isQuoteDivider);
  // Divider bounds guarantee no QUOTE_DIVIDER sentinel on either side of the split.
  const beforeItems = orderedItems.slice(
    0,
    dividerIndex,
  ) as QuoteDocumentConfig[];
  const afterItems = orderedItems.slice(
    dividerIndex + 1,
  ) as QuoteDocumentConfig[];

  // Derived from the ghost-adjusted orderedItems (not the raw before/afterDocuments
  // props) so the count doesn't transiently drop by one during Replace's
  // delete-then-create gap.
  const documentCount = beforeItems.length + afterItems.length;
  const isEmpty = documentCount === 0 && pendingUploads.length === 0;
  const showHeader = !isReadOnly || documentCount > 0;
  const beforePendingUploads = pendingUploads.filter(
    (pending) => pending.position === QuoteDocumentConfigPositionEnum.BEFORE,
  );
  const afterPendingUploads = pendingUploads.filter(
    (pending) => pending.position === QuoteDocumentConfigPositionEnum.AFTER,
  );

  const renderRow = (doc: QuoteDocumentConfig, index: number) => (
    <QuoteDocumentRow
      key={doc.id}
      doc={doc}
      index={index}
      mode={mode}
      quoteDocumentTypes={quoteDocumentTypes}
      isReadOnly={isReadOnly}
      isChangingType={changingTypeDocId === doc.id}
      isTogglingAutoAdd={togglingAutoAddDocId === doc.id}
      isOpening={openingDocId === doc.id}
      isDownloading={downloadingDocId === doc.id}
      replacingFile={activeReplacements.get(doc.id)?.file ?? null}
      onMove={handleMove}
      onDropEnd={handleDropEnd}
      onOpenDocument={handleOpenDocument}
      onDownloadDocument={handleDownloadDocument}
      onRemoveDocument={onDeleteDocument}
      onRequestReplace={handleRequestReplace}
      onChangeType={handleChangeType}
      onToggleAutoAdd={
        mode === QuoteDocumentsMode.SETTINGS ? handleToggleAutoAdd : undefined
      }
    />
  );

  const renderPendingRow = (pending: QuoteDocumentUpload) => (
    <PendingDocumentRow
      key={pending.localId}
      pending={pending}
      mode={mode}
      quoteDocumentTypes={quoteDocumentTypes}
      onSelectType={(documentTypeId) =>
        handlePendingTypeSelected(pending, documentTypeId)
      }
      onRetry={() => handleRetryPendingUpload(pending)}
      onRemove={() => removePendingUpload(pending.localId)}
    />
  );

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleFilesSelected(event.target.files, uploadPositionRef.current);
    }
    event.target.value = '';
  };

  const toolbarSharedProps = {
    fileInputRef,
    replaceInputRef,
    acceptAttr,
    onUploadClick: () =>
      handleOpenFilePicker(QuoteDocumentConfigPositionEnum.AFTER),
    onFileInputChange: handleFileInputChange,
    onReplaceInputChange: handleReplaceFileChange,
  };

  const renderToolbar = () => {
    if (mode === QuoteDocumentsMode.QUOTE) {
      const {
        availableDocuments,
        isAvailableDocumentsLoading,
        isImportingDocuments,
        onImportDocuments,
      } = props;
      return (
        <QuoteDocumentsToolbar
          {...toolbarSharedProps}
          mode={mode}
          availableDocuments={availableDocuments}
          isAvailableDocumentsLoading={isAvailableDocumentsLoading}
          isImportingDocuments={isImportingDocuments}
          onImportDocuments={onImportDocuments}
        />
      );
    }
    return <QuoteDocumentsToolbar {...toolbarSharedProps} mode={mode} />;
  };

  return (
    <MBox>
      {!isReadOnly && renderToolbar()}

      <MBox overflowX="auto">
        <MBox
          minW={
            mode === QuoteDocumentsMode.SETTINGS
              ? SETTINGS_ROW_GRID_MIN_WIDTH
              : ROW_GRID_MIN_WIDTH
          }
        >
          {showHeader && <QuoteDocumentListHeader mode={mode} />}

          <MBox
            position="relative"
            onDragEnter={handleZoneDragEnter('before')}
            onDragOver={handleZoneDragOver}
            onDragLeave={handleZoneDragLeave('before')}
            onDrop={handleZoneDrop('before')}
          >
            {beforeItems.map((doc, index) => renderRow(doc, index))}
            {beforePendingUploads.map(renderPendingRow)}
            {!isReadOnly &&
              beforeItems.length === 0 &&
              beforePendingUploads.length === 0 && (
                <EmptyRegionPlaceholder
                  label="Documents attached here will be displayed before the quote."
                  onClick={() =>
                    handleOpenFilePicker(QuoteDocumentConfigPositionEnum.BEFORE)
                  }
                />
              )}
            {!isReadOnly && dragZone === 'before' && <DropZoneOverlay />}
          </MBox>

          {dividerIndex !== -1 &&
            !(isEmpty && isReadOnly) &&
            (mode === QuoteDocumentsMode.QUOTE ? (
              <QuoteDocumentRow
                isDivider
                index={dividerIndex}
                mode={mode}
                isReadOnly={isReadOnly}
                isOpening={isOpeningQuote}
                isDownloading={isDownloadingQuote}
                onMove={handleMove}
                onDropEnd={handleDropEnd}
                onOpenQuote={handleOpenQuote}
                onDownloadQuote={handleDownloadQuote}
              />
            ) : (
              <QuoteDocumentRow
                isDivider
                index={dividerIndex}
                mode={mode}
                isReadOnly={isReadOnly}
                onMove={handleMove}
                onDropEnd={handleDropEnd}
              />
            ))}

          <MBox
            position="relative"
            onDragEnter={handleZoneDragEnter('after')}
            onDragOver={handleZoneDragOver}
            onDragLeave={handleZoneDragLeave('after')}
            onDrop={handleZoneDrop('after')}
          >
            {afterItems.map((doc, index) =>
              renderRow(doc, dividerIndex + 1 + index),
            )}
            {afterPendingUploads.map(renderPendingRow)}
            {!isReadOnly &&
              afterItems.length === 0 &&
              afterPendingUploads.length === 0 && (
                <EmptyRegionPlaceholder
                  label="Documents attached here will be displayed after the quote."
                  onClick={() =>
                    handleOpenFilePicker(QuoteDocumentConfigPositionEnum.AFTER)
                  }
                />
              )}
            {!isReadOnly && dragZone === 'after' && <DropZoneOverlay />}
          </MBox>

          {isEmpty && isReadOnly && (
            <MText fontSize="sm" color="tGray.darkPurple">
              No documents added
            </MText>
          )}
        </MBox>
      </MBox>

      {documentCount > 0 && (
        <MFlex
          alignItems="center"
          mt={4}
          pt={4}
          borderTop="1px solid"
          borderColor="tGray.lightGray"
        >
          <MText fontSize="xs" color="tGray.darkPurple">
            {documentCount} {pluralize('document', documentCount)}
            {mode === QuoteDocumentsMode.QUOTE ? ' on this quote' : ''}
          </MText>
        </MFlex>
      )}
    </MBox>
  );
};
